import { useState } from "react";
import { Link } from "react-router-dom";
import { ArrowRight, Check } from "lucide-react";
import { orderedCategories, school, lessonCount, extraFeatures } from "../mock/mock";

export default function PricesCarousel() {
  const [active, setActive] = useState(0);
  const [duration, setDuration] = useState(40);
  const cats = orderedCategories.filter((c) => (c.duration || 60) === duration);
  const cat = cats[Math.min(active, cats.length - 1)];
  const cols = cat.items.length >= 4 ? "lg:grid-cols-4" : cat.items.length === 3 ? "lg:grid-cols-3" : "lg:grid-cols-2 lg:max-w-4xl lg:mx-auto";

  return (
    <section className="py-24 relative overflow-hidden" data-testid="home-prices-section">
      <div className="absolute inset-0 bg-[#f6f9fc]" />
      <div className="absolute -top-40 left-1/2 -translate-x-1/2 w-[60rem] h-[30rem] rounded-full bg-[#ffce3a]/20 blur-3xl" />
      <div className="relative max-w-7xl mx-auto px-5">
        <div className="text-center max-w-3xl mx-auto mb-10">
          <span className="inline-block px-4 py-1.5 rounded-full bg-[#0B2545] text-[#ffce3a] font-bold tracking-[0.18em] text-xs uppercase">Våra paket & priser</span>
        </div>

        {/* Duration toggle */}
        <div className="flex justify-center mb-5">
          <div className="inline-flex items-center gap-1 p-1 rounded-full bg-[#0B2545] shadow-sm" role="tablist" aria-label="Lektionslängd">
            {[40, 60].map((d) => (
              <button key={d} role="tab" aria-selected={duration === d} data-testid={`price-duration-${d}`} onClick={() => setDuration(d)} className={`px-5 py-2 rounded-full text-sm font-bold transition-colors ${duration === d ? "bg-[#ffce3a] text-[#0B2545]" : "text-white/80 hover:text-white"}`}>
                {d} min lektioner
              </button>
            ))}
          </div>
        </div>

        {/* Tabs */}
        <div className="flex justify-center mb-12 overflow-x-auto no-scrollbar -mx-5 px-5">
          <div className="inline-flex items-center gap-1 p-1.5 rounded-2xl bg-white border border-slate-200 shadow-sm shrink-0" role="tablist">
            {cats.map((c, i) => (
              <button
                key={c.key}
                role="tab"
                aria-selected={i === active}
                data-testid={`price-tab-${c.key}`}
                onClick={() => setActive(i)}
                className={`relative px-5 sm:px-7 py-2.5 rounded-xl font-bold text-sm sm:text-base transition-colors overflow-hidden ${i === active ? "bg-[#0B2545] text-white" : "text-slate-600 hover:text-[#0B2545] hover:bg-slate-50"}`}
              >
                {c.title.replace(" 40 min", "")}
              </button>
            ))}
          </div>
        </div>

        <div key={cat.key} className={`flex lg:grid overflow-x-auto lg:overflow-visible snap-x snap-mandatory scroll-px-5 -mx-5 px-5 pb-4 lg:mx-0 lg:px-0 lg:pb-0 gap-5 lg:gap-7 items-stretch price-slide no-scrollbar ${cols}`}>
          {cat.items.map((p, idx) => (
            <div
              key={p.name}
              style={{ animationDelay: `${idx * 90}ms` }}
              data-testid={`price-card-${p.name.replace(/\s+/g, "-").toLowerCase()}`}
              className={`price-card snap-center shrink-0 w-[82vw] sm:w-[58vw] lg:w-auto relative flex flex-col rounded-3xl p-7 md:p-8 border-2 transition-all hover:-translate-y-2 hover:shadow-2xl ${p.popular ? "bg-[#0B2545] border-[#ffce3a] text-white shadow-2xl shadow-[#0B2545]/30 lg:scale-105 lg:z-10" : "bg-white border-slate-100 shadow-lg shadow-slate-200/60"}`}
            >
              {p.popular && (
                <span className="absolute -top-4 left-1/2 -translate-x-1/2 px-5 py-1.5 rounded-full bg-[#ffce3a] text-[#0B2545] text-xs font-extrabold uppercase tracking-wider shadow-md">Populärast</span>
              )}
              <p className={`text-xs font-bold uppercase tracking-[0.18em] ${p.popular ? "text-slate-300" : "text-slate-400"}`}>{p.name}</p>
              <div className="mt-3 flex items-end gap-3">
                <span className={`font-display text-[5.5rem] leading-[0.85] font-black tracking-tight ${p.popular ? "text-[#ffce3a]" : "text-[#0B2545]"}`} data-testid="carousel-lesson-count">{lessonCount(p)}</span>
                <span className={`pb-1.5 text-base font-extrabold leading-tight ${p.popular ? "text-white" : "text-[#0B2545]"}`}>körlektioner<br /><span className={`text-sm font-semibold ${p.popular ? "text-slate-300" : "text-slate-500"}`}>à {cat.duration || 60} minuter</span></span>
              </div>
              <div className={`mt-5 pt-5 border-t ${p.popular ? "border-white/15" : "border-slate-100"}`}>
                <span className={`font-display text-3xl font-black tracking-tight ${p.popular ? "text-white" : "text-[#0B2545]"}`}>{p.price}</span>
                <div className="mt-2 flex flex-wrap items-center gap-2">
                  <span className="text-sm line-through text-slate-400">Ord. pris {p.ord}</span>
                  <span className={`px-2.5 py-1 rounded-lg text-sm font-extrabold ${p.popular ? "bg-emerald-400/20 text-emerald-300" : "bg-emerald-50 text-emerald-700"}`}>Du sparar {p.save.replace("Rabatt ", "")}</span>
                </div>
              </div>
              <ul className={`mt-6 space-y-3 flex-1 ${p.popular ? "text-slate-200" : "text-slate-700"}`}>
                {extraFeatures(p).map((f) => (
                  <li key={f} className="flex items-start gap-3 text-[15px] font-medium">
                    <span className={`mt-0.5 w-6 h-6 rounded-full flex items-center justify-center flex-shrink-0 ${p.popular ? "bg-[#ffce3a] text-[#0B2545]" : "bg-[#0B2545] text-[#ffce3a]"}`}><Check size={14} strokeWidth={3} /></span>
                    {f}
                  </li>
                ))}
              </ul>
              <a href={cat.key === "kor" ? school.bookingUrlKorpaket : school.bookingUrl} target="_blank" rel="noopener noreferrer" className={`group mt-8 inline-flex items-center justify-center gap-2 px-6 py-4 rounded-xl font-extrabold text-base transition-colors ${p.popular ? "bg-[#ffce3a] text-[#0B2545] hover:bg-[#ffd85f]" : "bg-[#0B2545] text-white hover:bg-[#13315C]"}`}>
                Välj paket
                <ArrowRight size={18} className="group-hover:translate-x-1 transition-transform" />
              </a>
            </div>
          ))}
        </div>

        <div className="flex justify-center gap-2 mt-10">
          {cats.map((c, i) => (
            <button key={c.key} aria-label={c.title} onClick={() => setActive(i)} className={`h-2.5 rounded-full transition-all ${i === active ? "w-8 bg-[#0B2545]" : "w-2.5 bg-slate-300 hover:bg-slate-400"}`} />
          ))}
        </div>

        <div className="text-center mt-10">
          <Link to="/priser" className="group inline-flex items-center gap-2 px-8 py-4 rounded-xl bg-[#0B2545] text-white font-extrabold text-lg hover:bg-[#13315C] transition-colors shadow-lg">
            Se alla paket och priser
            <ArrowRight size={20} className="group-hover:translate-x-1 transition-transform" />
          </Link>
        </div>
      </div>
    </section>
  );
}
